import { sessionFromLoginResponse } from '@/auth/authSession';
import type { KincodeGoogleCredential } from '@/auth/core/googleSsoTypes';
import { mapLoginHttpError } from '@/auth/core/postLogin';
import type { AuthSession, LoginError, LoginSuccessResponse } from '@/auth/types';
import { Configs } from '@/config';

export type GoogleSsoResult =
  | { ok: true; session: AuthSession }
  | { ok: false; error: LoginError }
  | { ok: false; notOnboarded: true; message: string };

/**
 * Exchange a verifiable Google credential for Kincode tokens via coolture_auth.
 * @param {KincodeGoogleCredential} credential
 * @returns {Promise<GoogleSsoResult>}
 */
export async function postGoogleSso(
  credential: KincodeGoogleCredential
): Promise<GoogleSsoResult> {
  if (!Configs.googleSsoApiUrl) {
    return {
      ok: false,
      error: {
        code: 'misconfigured',
        message: 'Google sign-in is not configured (VITE_AUTH_BASE_URL).',
      },
    };
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Kincode-Client-Id': Configs.kincodeClientId,
  };

  if (Configs.unauthenticatedApiToken) {
    headers.Authorization = `Bearer ${Configs.unauthenticatedApiToken}`;
  }

  let response: Response;

  try {
    response = await fetch(Configs.googleSsoApiUrl, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({
        authType: 'Google',
        data: credential,
        timestamp: new Date().toISOString(),
      }),
    });
  } catch {
    return {
      ok: false,
      error: { code: 'network', message: 'Unable to reach the auth service.' },
    };
  }

  if (response.status === 201) {
    let message = 'Your organization is not onboarded yet. Contact Kincode to get access.';
    try {
      const payload = (await response.json()) as { message?: string };
      if (payload?.message?.trim()) {
        message = payload.message.trim();
      }
    } catch {
      // keep default message
    }

    return { ok: false, notOnboarded: true, message };
  }

  if (!response.ok) {
    let serverMessage: string | undefined;
    try {
      const payload = (await response.json()) as { message?: string };
      serverMessage = payload?.message;
    } catch {
      serverMessage = undefined;
    }

    if (response.status === 503) {
      return {
        ok: false,
        error: {
          code: 'server',
          message: serverMessage?.trim() || 'Google sign-in is temporarily unavailable.',
        },
      };
    }

    return { ok: false, error: mapLoginHttpError(response.status, serverMessage) };
  }

  let payload: LoginSuccessResponse;
  try {
    payload = (await response.json()) as LoginSuccessResponse;
  } catch {
    return {
      ok: false,
      error: { code: 'server', message: 'Unexpected response from auth service.' },
    };
  }

  if (!payload?.bearerToken || payload.companyID == null || payload.employeeID == null) {
    return {
      ok: false,
      error: { code: 'server', message: 'Incomplete SSO response from auth service.' },
    };
  }

  return {
    ok: true,
    session: sessionFromLoginResponse(payload),
  };
}
